import { useRef, useLayoutEffect } from "react";

// 数字のみの文字列を 3 桁区切り表示に変換する (例: "12000" -> "12,000")
const formatWithCommas = (raw) => {
  if (raw === "") return "";
  return Number(raw).toLocaleString();
};

// 文字列中の pos 文字目より前にある数字の個数を数える
const countDigitsBefore = (str, pos) => {
  let count = 0;
  for (let i = 0; i < pos && i < str.length; i++) {
    if (str[i] >= "0" && str[i] <= "9") count++;
  }
  return count;
};

// 文字列中で digitCount 番目の数字の直後の位置を返す
const findPosForDigitCount = (str, digitCount) => {
  if (digitCount <= 0) return 0;
  let count = 0;
  for (let i = 0; i < str.length; i++) {
    if (str[i] >= "0" && str[i] <= "9") {
      count++;
      if (count === digitCount) return i + 1;
    }
  }
  return str.length;
};

/**
 * InputField - テキスト入力フィールド (単位ラベル付き)
 *
 * サイズ・フォント系は上位コンポーネントから props で受け取る。
 * デフォルト値は通常の使用に合わせた値を設定してある。
 *
 * 入力制限: numericOnly=true のとき半角数字のみ受け付ける (デフォルト)。
 * numericOnly=true のとき、表示上は3桁区切り（カンマ）で表示する。
 * onChange に渡す値・state 側はカンマを含まない数字文字列のまま変化しない。
 * Enter キー: 同一ページ内の次の input へフォーカスを移動する。
 *
 * @param {string} value          - 入力値 (カンマなしの数字文字列)
 * @param {function} onChange     - 変更ハンドラ
 * @param {string} placeholder    - プレースホルダー
 * @param {string} type           - input の type 属性 (例: 'text', 'password')
 * @param {boolean} numericOnly   - true のとき半角数字のみ入力可・カンマ区切り表示 (デフォルト true)
 * @param {string} sizeClass      - padding / font-size クラス (例: 'px-2 py-1 text-sm')
 * @param {string} textAlignClass - テキスト揃えクラス (例: 'text-right')
 * @param {string} widthClass     - 幅クラス (例: 'w-52')
 * @param {string} unit           - 単位ラベル (例: '円', '%')。空文字の場合は非表示
 * @param {string} unitClass      - 単位ラベルのスタイルクラス
 * @param {string} borderClass    - ボーダー・フォーカスクラス
 * @param {number} maxLength      - 入力文字数の上限 (指定時のみ設定)
 * @param {boolean} disabled      - true のとき非活性化し、ResultField同様の暗転表示にする（集計ロック時等）
 */
function InputField({
  value,
  onChange,
  placeholder = "",
  type = "text",
  numericOnly = true,
  sizeClass = "px-2 py-1 text-sm",
  textAlignClass = "text-right",
  widthClass = "",
  unit = "",
  unitClass = "text-sm text-gray-600",
  borderClass = "border border-gray-600 focus:outline-none focus:border-black",
  maxLength,
  disabled = false,
}) {
  const inputRef = useRef(null);
  // カンマ挿入後にカーソル位置を補正するための保持先
  const pendingCursorPos = useRef(null);

  const displayValue = numericOnly ? formatWithCommas(value) : value;

  useLayoutEffect(() => {
    if (pendingCursorPos.current != null && inputRef.current) {
      inputRef.current.setSelectionRange(pendingCursorPos.current, pendingCursorPos.current);
      pendingCursorPos.current = null;
    }
  }, [displayValue]);

  const handleKeyDown = (e) => {
    // Enter キーで次の input へ移動 (numericOnly に関わらず共通)
    // 次の input が存在するときのみ preventDefault する。
    // 最後の input の場合は preventDefault せず、form の onSubmit に委ねる。
    if (e.key === "Enter") {
      const inputs = Array.from(document.querySelectorAll("input"));
      const idx = inputs.indexOf(e.currentTarget);
      if (idx !== -1 && idx + 1 < inputs.length) {
        e.preventDefault();
        inputs[idx + 1].focus();
      }
      return;
    }

    if (!numericOnly) return;

    // 半角数字・操作キー以外を弾く
    const allowedKeys = [
      "Backspace", "Delete", "Tab",
      "ArrowLeft", "ArrowRight", "ArrowUp", "ArrowDown",
      "Home", "End",
    ];
    const isDigit = e.key >= "0" && e.key <= "9";
    if (!isDigit && !allowedKeys.includes(e.key)) {
      e.preventDefault();
    }
  };

  const handleChange = (e) => {
    if (!numericOnly) {
      onChange(e);
      return;
    }
    const inputStr = e.target.value;
    const cursorPos = e.target.selectionStart;
    // ペースト等で混入した非数字文字を除去
    const filtered = inputStr.replace(/[^0-9]/g, "");

    // カンマ増減によるカーソル位置のずれを、カーソル前の数字の桁数を基準に補正する
    const digitsBeforeCursor = countDigitsBefore(inputStr, cursorPos);
    const newDisplayValue = formatWithCommas(filtered);
    pendingCursorPos.current = findPosForDigitCount(newDisplayValue, digitsBeforeCursor);

    onChange({ ...e, target: { ...e.target, value: filtered } });
  };

  return (
    <div className="flex items-center m-0 p-0">
      <input
        ref={inputRef}
        type={type}
        inputMode={numericOnly ? "numeric" : undefined}
        value={displayValue}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        maxLength={maxLength}
        disabled={disabled}
        className={`${borderClass} ${sizeClass} ${textAlignClass} ${widthClass} ${disabled ? "bg-gray-50 text-gray-500 cursor-not-allowed" : ""}`}
      />
      {unit && <span className={`ml-1 p-1 ${unitClass}`}>{unit}</span>}
    </div>
  );
}

export default InputField;
